import React, { useEffect, useState } from 'react';
import { Lightbulb, Sparkles } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { canWriteRecommendations } from '../utils/roles';

const getScoreDisplay = (score, req = 15, action = null, engine = 'generic') => {
  let spam = false;
  if (engine === 'rspamd' && action) spam = !['greylist', 'no action', 'no_action'].includes(action.toLowerCase().replace(/\s+/g, '_'));
  else if (engine === 'overall') spam = score >= req || (action && !['greylist', 'no action', 'no_action'].includes(action.toLowerCase().replace(/\s+/g, '_')));
  else spam = score >= req;
  if (spam) return { label: 'SPAM', badge: 'bg-red-100 text-red-700', bg: 'bg-red-50' };
  if (score >= req * 0.7) return { label: 'SUSPICIOUS', badge: 'bg-yellow-100 text-yellow-700', bg: 'bg-yellow-50' };
  return { label: 'CLEAN', badge: 'bg-green-100 text-green-700', bg: 'bg-green-50' };
};

const severityStyle = (s) => {
  if (s === 'critical' || s === 'high') return 'border-red-500 bg-red-50';
  if (s === 'medium') return 'border-yellow-500 bg-yellow-50';
  return 'border-blue-500 bg-blue-50';
};

function ScanResultsView({ scanResult }) {
  const { user } = useAuthStore();
  const canGenerateRecommendation = canWriteRecommendations(user);
  const [recs, setRecs] = useState({});
  const [modal, setModal] = useState(null);
  const [rec, setRec] = useState(null);
  const [loadingRec, setLoadingRec] = useState(false);
  const [generatingRec, setGeneratingRec] = useState(false);
  const [advice, setAdvice] = useState(null);
  const [loadingAdvice, setLoadingAdvice] = useState(false);

  useEffect(() => {
    if (!scanResult) return;
    const symbols = [];
    if (scanResult.results?.rspamd?.symbols) Object.keys(scanResult.results.rspamd.symbols).forEach(n => symbols.push({ name: n, engine: 'rspamd' }));
    if (scanResult.results?.spamassassin?.symbols) scanResult.results.spamassassin.symbols.forEach(n => symbols.push({ name: n, engine: 'spamassassin' }));
    if (!symbols.length) return;
    axios.post('/recommendations/symbols/batch', { symbols }).then(r => setRecs(r.data.recommendations || {})).catch(() => {});
  }, [scanResult]);

  const hasRec = (name, engine) => recs[`${name}_${engine}`] !== undefined;
  const recCount = (engine, symbols) => {
    if (!symbols) return 0;
    if (engine === 'rspamd' && typeof symbols === 'object') return Object.keys(symbols).filter(s => hasRec(s, engine)).length;
    if (engine === 'spamassassin' && Array.isArray(symbols)) return symbols.filter(s => hasRec(s, engine)).length;
    return 0;
  };

  const openDetails = async (symbol, data, engine) => {
    setModal({ symbol, data, engine }); setRec(null);
    try { setLoadingRec(true); const r = await axios.get(`/recommendations/symbol/${engine}/${encodeURIComponent(symbol)}`); setRec(r.data.recommendation); }
    catch { setRec(null); } finally { setLoadingRec(false); }
  };

  const generateRec = async () => {
    if (!modal) return;
    try {
      setGeneratingRec(true);
      const r = await axios.post('/recommendations/generate', { symbolName: modal.symbol, engine: modal.engine, symbolData: modal.data });
      setRec(r.data.recommendation);
      setRecs(prev => ({ ...prev, [`${modal.symbol}_${modal.engine}`]: true }));
      toast.success(r.data.source === 'generated' ? 'Recommendation generated and saved' : 'Recommendation found');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to generate'); }
    finally { setGeneratingRec(false); }
  };

  if (!scanResult) return null;

  const SymbolList = ({ symbols, engine, isObject = true }) => {
    const entries = isObject ? Object.entries(symbols).sort(([,a],[,b]) => Math.abs(b.score||0) - Math.abs(a.score||0)) :
      symbols.map(s => [s, scanResult.results.spamassassin.symbol_details?.[s] || { score: 'N/A', description: `SpamAssassin rule: ${s}` }]);
    return (
      <div className="max-h-96 overflow-y-auto border rounded divide-y">
        {entries.map(([sym, data], idx) => {
          const score = data.score; const hasScore = score !== 'N/A' && score !== undefined;
          return (
            <button key={isObject ? sym : idx} onClick={() => openDetails(sym, data, engine)} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 min-w-0">
                  <span className="font-medium truncate">{sym}</span>
                  {hasRec(sym, engine) && <Lightbulb className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />}
                </div>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${hasScore && score > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {hasScore ? (score > 0 ? '+' : '') + parseFloat(score).toFixed(2) : 'N/A'}
                </span>
              </div>
              {data.description && <p className="text-xs text-gray-400 truncate mt-0.5">{data.description}</p>}
            </button>
          );
        })}
      </div>
    );
  };

  const fetchAdvice = async () => {
    try {
      setLoadingAdvice(true);
      const r = await axios.post('/scan/advice', { scanResult });
      setAdvice(r.data.advice);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to get AI advice');
    } finally { setLoadingAdvice(false); }
  };

  const priorityStyle = (p) => {
    if (p === 'critical') return 'bg-red-100 text-red-700';
    if (p === 'high') return 'bg-orange-100 text-orange-700';
    if (p === 'medium') return 'bg-yellow-100 text-yellow-700';
    return 'bg-blue-100 text-blue-700';
  };

  return (
    <div>
      {scanResult.overall && (() => {
        const d = getScoreDisplay(scanResult.overall.score, 15, scanResult.overall.action, 'overall');
        return (
          <div className={`${d.bg} rounded-lg p-6 mb-6 flex items-center justify-between`}>
            <div className="flex items-center gap-4">
              <p className="text-4xl font-bold">{scanResult.overall.score?.toFixed(2) || '0.00'}</p>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${d.badge}`}>{d.label}</span>
              <p className="text-sm text-gray-600 ml-2">Action: {scanResult.overall.action}</p>
            </div>
            {!advice && (
              <button onClick={fetchAdvice} disabled={loadingAdvice}
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition text-sm font-medium disabled:opacity-50">
                {loadingAdvice ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Analyzing...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Get AI Advice</>
                )}
              </button>
            )}
          </div>
        );
      })()}

      {advice && (
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" /> AI Deliverability Advice</h3>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${advice.overall_verdict === 'pass' ? 'bg-green-100 text-green-700' : advice.overall_verdict === 'fail' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {advice.overall_verdict?.toUpperCase()}
            </span>
          </div>
          <p className="text-gray-700 mb-3">{advice.summary}</p>
          {advice.score_explanation && <p className="text-sm text-gray-500 mb-4">{advice.score_explanation}</p>}

          {advice.issues?.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Issues</h4>
              <div className="space-y-2">
                {advice.issues.map((issue, i) => (
                  <div key={i} className="border rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityStyle(issue.priority)}`}>{issue.priority}</span>
                      <span className="text-xs text-gray-400">{issue.area}</span>
                    </div>
                    <p className="text-sm font-medium">{issue.problem}</p>
                    <p className="text-sm text-gray-600 mt-1">{issue.fix}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {advice.quick_wins?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Quick Wins</h4>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                {advice.quick_wins.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {scanResult.results?.rspamd && (
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-blue-600">Rspamd</h4>
              {recCount('rspamd', scanResult.results.rspamd.symbols) > 0 && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{recCount('rspamd', scanResult.results.rspamd.symbols)} tips</span>}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div><span className="text-gray-500 block">Score</span><span className="text-lg font-bold">{scanResult.results.rspamd.score?.toFixed?.(2) ?? 'N/A'}</span></div>
              <div><span className="text-gray-500 block">Rules</span><span className="text-lg font-bold">{scanResult.results.rspamd.rules_matched || Object.keys(scanResult.results.rspamd.symbols || {}).length}</span></div>
            </div>
            {scanResult.results.rspamd.symbols && <SymbolList symbols={scanResult.results.rspamd.symbols} engine="rspamd" />}
          </div>
        )}

        {scanResult.results?.spamassassin && (
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-yellow-600">SpamAssassin</h4>
              {recCount('spamassassin', scanResult.results.spamassassin.symbols) > 0 && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{recCount('spamassassin', scanResult.results.spamassassin.symbols)} tips</span>}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div><span className="text-gray-500 block">Rules</span><span className="text-lg font-bold">{scanResult.results.spamassassin.rules_matched || scanResult.results.spamassassin.symbols?.length || 0}</span></div>
              <div><span className="text-gray-500 block">Status</span><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${scanResult.results.spamassassin.is_spam ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{scanResult.results.spamassassin.is_spam ? 'SPAM' : 'CLEAN'}</span></div>
            </div>
            {scanResult.results.spamassassin.symbols && <SymbolList symbols={scanResult.results.spamassassin.symbols} engine="spamassassin" isObject={false} />}
          </div>
        )}

        {scanResult.results?.ai && (
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <h4 className="font-semibold text-purple-600 mb-3">AI (GPT) Analysis</h4>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div><span className="text-gray-500 block">Probability</span><span className="text-lg font-bold">{scanResult.results.ai.probability != null ? (scanResult.results.ai.probability * 100).toFixed(1) + '%' : 'N/A'}</span></div>
              <div><span className="text-gray-500 block">Class</span><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${scanResult.results.ai.is_spam ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{scanResult.results.ai.is_spam ? 'SPAM' : 'HAM'}</span></div>
            </div>
            {scanResult.results.ai.reasoning && <div className="border-l-4 border-purple-500 bg-gray-50 p-3 rounded text-sm italic mb-3">"{scanResult.results.ai.reasoning}"</div>}
            {scanResult.results.ai.categories?.length > 0 && <div className="flex flex-wrap gap-1">{scanResult.results.ai.categories.map((c,i) => <span key={i} className="px-2 py-0.5 rounded-full text-xs border border-purple-200 text-purple-700">{c.replace('GPT_','')}</span>)}</div>}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setModal(null); setRec(null); }}>
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">{modal.symbol}</h3>
            <p className="text-sm text-gray-500 mb-4">Engine: {modal.engine}</p>
            <hr className="mb-4" />
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-blue-600 mb-3">Technical Details</h4>
                <p className="text-sm"><span className="text-gray-500">Score:</span> <strong>{modal.data?.score || 0}</strong></p>
                {modal.data?.description && <div className="bg-gray-50 p-3 rounded text-sm mt-2">{modal.data.description}</div>}
                {modal.data?.options?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{modal.data.options.map((o,i) => <span key={i} className="px-2 py-0.5 rounded-full text-xs border">{o}</span>)}</div>}
              </div>
              <div>
                {(loadingRec || generatingRec) ? (
                  <>
                    <h4 className="font-semibold text-purple-600 flex items-center gap-1 mb-3">
                      {generatingRec ? <Sparkles className="w-4 h-4" /> : <Lightbulb className="w-4 h-4" />}
                      {generatingRec ? 'Generating with GPT...' : 'How to Fix'}
                    </h4>
                    <div className="space-y-2"><div className="h-6 w-48 bg-gray-200 rounded animate-pulse" /><div className="h-16 bg-gray-200 rounded animate-pulse" /></div>
                  </>
                ) : rec ? (
                  <>
                    <h4 className="font-semibold text-purple-600 flex items-center gap-1 mb-3"><Lightbulb className="w-4 h-4" />How to Fix</h4>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-semibold">{rec.title}</h5>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rec.severity === 'critical' || rec.severity === 'high' ? 'bg-red-100 text-red-700' : rec.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{rec.severity?.toUpperCase()}</span>
                      </div>
                      {rec.description && <div className="bg-gray-50 p-3 rounded text-sm mb-3">{rec.description}</div>}
                      <div className={`p-3 rounded text-sm border-l-4 ${severityStyle(rec.severity)}`}><span className="text-xs text-gray-500 block mb-1">Recommended:</span>{rec.recommendation}</div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Sparkles className="w-10 h-10 text-purple-300 mb-2" />
                    <p className="text-sm text-gray-500 mb-3">No recommendation on file for this rule</p>
                    {canGenerateRecommendation && (
                      <button onClick={generateRec} className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition text-sm font-medium">
                        <Sparkles className="w-4 h-4" /> Ask GPT to explain
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-4"><button onClick={() => { setModal(null); setRec(null); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScanResultsView;
